/**
 * Standing of the client selected on the Contracts page, derived from that client's
 * contracts. The Gyurci theme paints the page background from it: green while the
 * client is clear, red once one of their contracts ended without redemption.
 *
 * A contract counts against the client when it ended with the shop keeping the item:
 * expired (`'!!'`), moved into stock (`'INV'`) or sold from stock (`'EXP'`) — the three
 * states the contracts grid already marks in red. Everything else leaves the client
 * clear: active (`''`/`null`) and passive (`'!'`) are still running, redeemed (`'OK'`)
 * ended the right way, and cancelled (`'ANL'`) never ran at all.
 *
 * Short contracts are excluded: the shop's 1-10 day loans expire as a matter of routine,
 * so only contracts longer than ten days carry the signal. The client's whole history
 * counts — one qualifying contract, however old, is enough.
 */

/** Contract fields this decision needs. `Durata` arrives as a number, but tolerate strings. */
export interface StandingContract {
    Statut?: string | null;
    Durata?: number | string | null;
    /** Owning client, used to tell this client's contracts from the previous one's. */
    Clnr?: number | null;
}

export type ClientStanding = 'clear' | 'expired';

/** Statuses of a contract the client did not redeem: expired, taken into stock, sold. */
export const UNREDEEMED_CONTRACT_STATUSES: readonly string[] = ['!!', 'INV', 'EXP'];

/** Longest contract still treated as a routine short loan; anything above this counts. */
export const SHORT_CONTRACT_MAX_DURATION_DAYS = 10;

/** True for an unredeemed contract long enough to count against the client. */
export const isCountedExpiredContract = (contract: StandingContract): boolean => {
    if (!contract.Statut || !UNREDEEMED_CONTRACT_STATUSES.includes(contract.Statut)) {
        return false;
    }
    const duration = Number(contract.Durata);
    return Number.isFinite(duration) && duration > SHORT_CONTRACT_MAX_DURATION_DAYS;
};

/**
 * Resolve the standing to paint. Returns `null` — paint nothing — whenever the answer is not
 * yet known, which is the whole point of this function: a wrong colour shown for a second and
 * then corrected is worse than no colour at all.
 *
 * Three states mean "not known", and each one produced a visible flash before it was handled:
 *
 * - **No client selected.** Nothing to say.
 * - **The contracts are not in.** `contractsLoaded` must be false while the request is in
 *   flight, before it starts, *and* if it failed. The page's query hook reports
 *   `{loading: false, data: undefined}` while it is idle, so "not loading" alone would read an
 *   empty list as a clean client and flash green in the second before the contracts arrive.
 * - **The contracts belong to the previous client.** After switching clients the last client's
 *   rows are still in hand until the new response lands, and painting them would flash *that*
 *   client's colour. Rows carry their owner in `Clnr`, so a list that is entirely someone
 *   else's is treated as not loaded.
 */
export const resolveClientStanding = (
    contracts: readonly StandingContract[],
    {
        selectedClientId,
        contractsLoaded,
    }: { selectedClientId: number | null; contractsLoaded: boolean },
): ClientStanding | null => {
    if (selectedClientId === null || !contractsLoaded) {
        return null;
    }
    // Rows without a `Clnr` are taken at face value: the endpoint is per-client already.
    const ownContracts = contracts.filter(
        contract =>
            contract.Clnr === null ||
            contract.Clnr === undefined ||
            contract.Clnr === selectedClientId,
    );
    if (contracts.length > 0 && ownContracts.length === 0) {
        return null;
    }
    return ownContracts.some(isCountedExpiredContract) ? 'expired' : 'clear';
};
